/**
 * Stable fingerprint of the authoritative state (FNV-1a over a canonical serialisation). Two runs
 * with the same config and command log must produce the same hash — the determinism tests and the
 * headless simulator rely on it. Not meant for hot paths.
 */
import type { GameState } from '../core/types';

function canonical(_key: string, value: unknown): unknown {
  if (value instanceof Map) return [...value.entries()];
  if (ArrayBuffer.isView(value)) return Array.from(value as unknown as ArrayLike<number>);
  return value;
}

export function hashState(state: GameState): string {
  // The static map is derived from the seed; hash only what the simulation mutates.
  const { map: _map, config: _config, ...dynamic } = state;
  return hashText(JSON.stringify(dynamic, canonical));
}

/** 64-bit (two 32-bit FNV-style lanes) hex digest of a string. */
export function hashText(text: string): string {
  let h1 = 0x811c9dc5;
  let h2 = 0x01000193;
  for (let i = 0; i < text.length; i++) {
    const c = text.charCodeAt(i);
    h1 = Math.imul(h1 ^ c, 0x01000193);
    h2 = Math.imul(h2 ^ c, 0x5bd1e995);
  }
  return (h1 >>> 0).toString(16).padStart(8, '0') + (h2 >>> 0).toString(16).padStart(8, '0');
}
